import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { ReactNode } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { displayCallerNumber } from "../call-display.util";
import { popCallerNumber } from "../incoming-call-pop.slice";
import {
  callCardsSx,
  callPopHeaderSx,
  callPopHeaderTextSx,
  callPopSx
} from "./dialstack-softphone-widget.styles";
import SoftphoneCallControls from "./softphone-call-controls.component";
import SoftphoneStatusButton from "./softphone-status-button.component";
import type { InboundCallMatch } from "./use-inbound-call-pops.hook";

// The unfolded pop for a call that is ringing in (ENG-2226): the purple
// panel, headed by the bar's control row — the user's status, what the phone
// is doing, the number, Decline, Answer, and the chevron that folds it down
// to the bar — with the call's card inside it. The SDK's own incoming screen
// is not shown; these buttons call the SDK directly.
export default function SoftphoneCallPop({
  match,
  onFold,
  children
}: {
  match: InboundCallMatch;
  onFold: () => void;
  // The call's card (patient hero, shared-number rows or unknown caller).
  children: ReactNode;
}) {
  const { connection } = useSoftphone();
  const { call, pop } = match;
  const number = displayCallerNumber(pop ? popCallerNumber(pop) : call.from);
  return (
    <Paper elevation={0} data-testid="softphone-call-pop" sx={callPopSx}>
      <Box className="suno-phone-drag-handle" sx={callPopHeaderSx}>
        <SoftphoneStatusButton connection={connection} hasCallActivity />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="span" sx={callPopHeaderTextSx}>
            {pop?.transfer ? "Transferred call" : "Incoming call"}
          </Typography>
          {number && (
            <Typography
              noWrap
              sx={{ fontSize: 12, color: "rgba(255, 255, 255, 0.78)" }}
            >
              {number}
            </Typography>
          )}
        </Box>
        <SoftphoneCallControls call={call} state="ringing" />
        <Tooltip title="Hide details">
          <IconButton
            aria-label="Hide details"
            className="suno-phone-no-drag"
            size="small"
            onClick={onFold}
            sx={{ color: "rgba(255, 255, 255, 0.85)" }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {/* Polite, so a call ringing in mid-call is read out without cutting
          across the conversation's own announcements. */}
      <Box aria-live="polite" sx={callCardsSx}>
        {children}
      </Box>
    </Paper>
  );
}
